import { emergencyCard } from "./emergency";

/**
 * v0.9.8 — two map buttons. No backend, no form, nothing stored or sent.
 *  🆘 แจ้งเหตุ — always shown, just above ▶ (bottom-right). Opens a box: the request service is
 *     not built yet ("ระบบนี้กำลังพัฒนา ยังรับแจ้งไม่ได้") + the same 3 emergency numbers as tel:
 *     links. Makes NO network request and never asks for location. Close: [ปิด] / outside / Esc.
 *  (🌀 จำลองพายุ moved to the top-right group, top-right.ts — v0.9.9 A4)
 * Both are placed from the live layout (measured), so they never sit on 💧 / สรุป / ▶ / the river
 * chip / the day bars / the sheet in any state — re-placed as soon as any of those moves (v0.9.10d).
 */
export function mountSosButton(): void {
  const sos = document.createElement("button");
  sos.type = "button"; sos.id = "sos-fab"; sos.className = "sos-fab";
  sos.setAttribute("aria-label", "แจ้งเหตุขอความช่วยเหลือ (ยังรับแจ้งไม่ได้ — ดูเบอร์โทรฉุกเฉิน)");
  sos.textContent = "🆘 แจ้งเหตุ";
  sos.addEventListener("click", openSosBox);

  document.body.append(sos);
  const place = (): void => { placeSos(sos); };
  place();
  window.addEventListener("resize", place);
  window.addEventListener("fs-top-layout", place);
  // v0.9.10d: re-placed the moment something it depends on changes — before that frame is drawn
  // (a MutationObserver runs before paint) — not on the next tick of a timer. The old 500-ms timer
  // alone let 🆘 ride the sheet into the ☀️ for up to half a second (longer on a busy phone) after
  // "📍 รอบตัวฉัน" unfolded the sheet. The timer stays only as a safety net.
  let queued = false;
  const soon = (): void => { if (queued) return; queued = true; queueMicrotask(() => { queued = false; place(); }); };
  const mo = new MutationObserver(soon);
  const ro = typeof ResizeObserver === "function" ? new ResizeObserver(soon) : null;
  ro?.observe(sos); // the Thai web font arriving changes its width
  const seen = new WeakSet<Element>();
  const watch = (): void => {
    const sheet = document.querySelector<HTMLElement>(".sheet"), fab = document.querySelector("#flow-fab"), st = document.querySelector(".tr-stack");
    if (sheet && !seen.has(sheet)) {
      seen.add(sheet);
      mo.observe(sheet, { attributes: true, attributeFilter: ["class", "style"] }); // snap / fold / drag
      sheet.addEventListener("transitionrun", soon);
      sheet.addEventListener("transitionend", soon);
      ro?.observe(sheet);
    }
    if (fab && !seen.has(fab)) { seen.add(fab); mo.observe(fab, { attributes: true, attributeFilter: ["class", "style", "hidden"] }); }
    if (st && !seen.has(st)) { seen.add(st); mo.observe(st, { attributes: true, childList: true, subtree: true, attributeFilter: ["class", "style", "hidden"] }); ro?.observe(st); }
    // ▶ / past-storm player bar: it grows after it opens (the source line fills in) — 🆘 sits above it
    for (const bar of document.querySelectorAll(".fa-bar")) if (!seen.has(bar)) { seen.add(bar); ro?.observe(bar); }
  };
  watch();
  // body: state classes (fa-active / overview / card-open / past-mode) + late-mounted pieces (top-right group, ▶ bar)
  new MutationObserver(() => { watch(); soon(); }).observe(document.body, { attributes: true, attributeFilter: ["class"], childList: true });
  window.setInterval(place, 500);
}

const visible = (el: Element | null): el is HTMLElement => {
  if (!el || (el as HTMLElement).hidden) return false;
  const cs = getComputedStyle(el), r = el.getBoundingClientRect();
  return cs.display !== "none" && cs.visibility !== "hidden" && r.width > 0 && r.height > 0;
};

function placeSos(b: HTMLElement): void {
  const W = window.innerWidth, H = window.innerHeight, h = b.offsetHeight, gap = 10;
  const fab = document.querySelector<HTMLElement>("#flow-fab");
  // phones: ▶ rides the sheet's top edge (a child of .sheet) → 🆘 rides it too, just above ▶, so both
  // move together while the sheet animates (no lag, never over 💧 / ▶)
  if (visible(fab) && fab.classList.contains("on-sheet") && fab.parentElement) {
    if (b.parentElement !== fab.parentElement) fab.parentElement.append(b);
    b.classList.add("on-sheet");
    // height + right come from CSS (.sos-fab.on-sheet: 10 px above ▶, same right edge) — v0.9.10d: the
    // old measured `top` could be taken before the stylesheet applied (the page script may run first)
    // and then stay wrong, off screen, until the next re-place
    b.style.right = ""; b.style.bottom = ""; b.style.top = "";
    // never on the top-right group (v0.9.9 A4): step to its left (the sheet spans the screen width) —
    // judged where it is now AND where the sliding sheet will stop (v0.9.10d), so it steps aside as the
    // slide starts instead of after it has already reached the ☀️
    const st = groupRect(), me = b.getBoundingClientRect(), dy = sheetTravel(fab.parentElement);
    const hits = (top: number): boolean => !!st && !(me.right <= st.left || me.left >= st.right || top + me.height <= st.top || top >= st.bottom);
    if (st && (hits(me.top) || hits(me.top + dy))) {
      const sheetR = fab.parentElement.getBoundingClientRect().right;
      b.style.right = `${Math.round(sheetR - st.left + 8)}px`;
    }
    return;
  }
  if (b.parentElement !== document.body) document.body.append(b);
  b.classList.remove("on-sheet"); b.style.top = "";
  let right: number, bottom: number;
  if (visible(fab)) {
    const r = fab.getBoundingClientRect();
    right = W - r.right; bottom = H - r.top + gap;          // right-aligned with ▶, just above it
  } else {
    // ▶ hidden (▶ playing / past storm): above the bottom player bar, else above the sheet, else the corner
    const barEl = [...document.querySelectorAll(".fa-bar")].find(visible);
    const sheet = document.querySelector(".sheet");
    const floor = barEl ? barEl.getBoundingClientRect().top : visible(sheet) && sheet.getBoundingClientRect().left < W / 2 ? sheet.getBoundingClientRect().top : H;
    right = W >= 820 && visible(sheet) && sheet.getBoundingClientRect().left > W / 2 ? W - sheet.getBoundingClientRect().left + 12 : 14;
    bottom = H - floor + gap;
  }
  b.style.right = `${Math.max(8, Math.round(right))}px`;
  b.style.bottom = `${Math.max(8, Math.round(Math.min(bottom, H - h - 70)))}px`;
  // v0.9.9 A4: never on the top-right group — step to its left when they would meet
  const st = groupRect(), me = b.getBoundingClientRect();
  if (st && !(me.right <= st.left || me.left >= st.right || me.bottom <= st.top || me.top >= st.bottom)) {
    b.style.right = `${Math.round(W - st.left + 8)}px`;
  }
}

/** How far (px, + = down) the sheet still slides before it stops: the end of its running transform
 *  transition (Web Animations; the end keyframe is resolved to px), 0 when it is not moving. */
function sheetTravel(sheet: HTMLElement): number {
  const ty = (t: string): number => new DOMMatrixReadOnly(t === "none" ? undefined : t).m42;
  try {
    for (const a of sheet.getAnimations?.() ?? []) {
      if (!(a instanceof CSSTransition) || a.transitionProperty !== "transform" || a.playState === "finished") continue;
      const kf = (a.effect as KeyframeEffect | null)?.getKeyframes() ?? [];
      const end = kf[kf.length - 1]?.transform;
      if (typeof end !== "string") continue;
      const d = ty(end) - ty(getComputedStyle(sheet).transform);
      if (Number.isFinite(d)) return d;
    }
  } catch { /* an end value DOMMatrix cannot read → judge by where it is now */ }
  return 0;
}

/** The top-right group's box, including the ⓘ when it sits beside the column (short phones). */
function groupRect(): { left: number; right: number; top: number; bottom: number } | null {
  const st = document.querySelector<HTMLElement>(".tr-stack");
  if (!st || getComputedStyle(st).display === "none") return null;
  const rs = [st, ...Array.from(st.children)].map((e) => e.getBoundingClientRect()).filter((r) => r.width > 0);
  if (!rs.length) return null;
  return { left: Math.min(...rs.map((r) => r.left)), right: Math.max(...rs.map((r) => r.right)), top: Math.min(...rs.map((r) => r.top)), bottom: Math.max(...rs.map((r) => r.bottom)) };
}

/* ── the box ─────────────────────────────────────────────────────────── */
function openSosBox(): void {
  if (document.querySelector(".sos-scrim")) return;
  const scrim = document.createElement("div");
  scrim.className = "sos-scrim";
  const box = document.createElement("div");
  box.className = "sos-box"; box.setAttribute("role", "dialog"); box.setAttribute("aria-modal", "true"); box.setAttribute("aria-labelledby", "sos-title");
  box.innerHTML = `<h2 id="sos-title" class="sos-title">🆘 แจ้งเหตุขอความช่วยเหลือ</h2>
    <p class="sos-dev">ระบบนี้กำลังพัฒนา ยังรับแจ้งไม่ได้</p>
    <p class="sos-call">ต้องการความช่วยเหลือตอนนี้ โทร</p>`;
  box.append(emergencyCard());
  const close = document.createElement("button");
  close.type = "button"; close.className = "btn sos-close"; close.textContent = "ปิด";
  box.append(close);
  scrim.append(box);
  const prev = document.activeElement as HTMLElement | null;
  const done = (): void => { scrim.remove(); document.removeEventListener("keydown", onKey); prev?.focus(); };
  const onKey = (e: KeyboardEvent): void => { if (e.key === "Escape") done(); };
  close.addEventListener("click", done);
  scrim.addEventListener("click", (e) => { if (e.target === scrim) done(); }); // tap outside the box
  document.addEventListener("keydown", onKey);
  document.body.append(scrim);
  close.focus();
}
